import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Plus, Trash2, ArrowLeft, GitBranch, Users, Search } from 'lucide-react';
import { AppDispatch, RootState } from '../../store/store';
import {
  fetchDistributionRules,
  createRule,
  updateRule,
  deleteRule,
  selectDistributionRules,
  selectDistributionLoading,
} from '../../store/slices/distributionSlice';
import { DistributionRule, getDistributionOptions } from '../../services/distributionService';
import { getAllIntegration } from '../../services/automationService';
import '../../styles/designSystem.css';
import './leadDistributionSettings.scss';
import logger from '../../utils/logger';

const OPERATORS = [
  { value: 'eq', label: 'Equal' },
  { value: 'ne', label: 'Not Equal' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'lt', label: 'Less Than' },
  { value: 'contain', label: 'Contains' },
  { value: 'doesNotContain', label: 'Does Not Contain' },
];

// Integration IDs for conditional field rendering
const FACEBOOK_INTEGRATION_ID = '64109561e6c7e737227a6384';
const LEAD_FORM_INTEGRATION_ID = '64109561e6c7e737227a6382';

const LeadDistributionSettings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const rules = useSelector(selectDistributionRules);
  const loading = useSelector(selectDistributionLoading);
  const teamMembers = useSelector((state: RootState) => state.whatsapp.team.members);
  const StoreData = useSelector((state: any) => state.rootReducers);
  const [showForm, setShowForm] = useState<boolean>(false);
  const [editingRule, setEditingRule] = useState<DistributionRule | null>(null);
  const [sources, setSources] = useState<{ value: string; label: string; isCustom?: boolean }[]>([]);
  const [ruleSearch, setRuleSearch] = useState<string>('');

  // Get custom sources from user preferences
  const customSources = React.useMemo(() => {
    const sources = StoreData?.userData?.userDetails?.userPreference?.customSource;
    if (!sources || !Array.isArray(sources)) return [];
    return sources.map((s: { name: string; value: string }) => ({
      value: `custom_${s.value}`,
      label: s.name,
      isCustom: true
    }));
  }, [StoreData?.userData?.userDetails?.userPreference?.customSource]);

  useEffect(() => {
    dispatch(fetchDistributionRules());
    fetchIntegrationSources();
  }, [dispatch]);

  // Merge custom sources when they change
  useEffect(() => {
    if (customSources.length > 0) {
      setSources(prev => {
        // Remove old custom sources and add new ones
        const withoutCustom = prev.filter((s: any) => !s.isCustom);
        return [...withoutCustom, ...customSources];
      });
    }
  }, [customSources]);

  // Fetch all integration sources dynamically (EXACTLY like automation does)
  const fetchIntegrationSources = async () => {
    try {
      logger.log('🔍 Starting integration data fetch...');

      // STEP 1: Make API call with same params as automation
      const response = await getAllIntegration({
        isAscending: false,
        page: 1,
        perPage: 50
      });

      // STEP 2: Check response.status like automation does
      if (response && response.status) {
        logger.log('✅ API response received:', response.data.data.length, 'integrations');

        // STEP 3: Define special sources
        const SPECIAL_SOURCES = ['custom_form', 'lead_form', 'csv'];

        // STEP 4: Apply EXACT same filtering logic as automation
        const filteredIntegrations = response.data.data.filter((integration: any) => {
          const { name, key, isActive, isDisplayable } = integration;

          // Must be active
          if (!isActive) return false;

          // If displayable, include it
          if (isDisplayable) return true;

          // If special source, include it
          if (SPECIAL_SOURCES.includes(key)) {
            logger.log(`🎯 Special source included: ${name}`);
            return true;
          }

          return false;
        });

        // STEP 5: Convert to dropdown format
        const formattedSources = filteredIntegrations.map((integration: any) => ({
          value: integration._id,
          label: integration.name
        }));

        // STEP 6: Add custom sources from user preferences
        const allSources = [...formattedSources, ...customSources];

        logger.log('📋 Final sources loaded:', allSources.length, allSources);
        setSources(allSources);
      }
    } catch (err) {
      console.error('❌ Integration fetch error:', err);
      // Fallback to basic sources if API fails
      setSources([
        { value: '64109561e6c7e737227a6382', label: 'Lead Form' },
        { value: '64109561e6c7e737227a6384', label: 'Facebook' },
        { value: '64109561e6c7e737227a6383', label: 'IndiaMART' },
        { value: '64109561e6c7e737227a6385', label: 'TradeIndia' },
        { value: '64109561e6c7e737227a637c', label: 'CSV' },
        ...customSources
      ]);
    }
  };

  const handleToggleStatus = async (rule: DistributionRule) => {
    // Only send required fields for status toggle
    // Exclude: organization, createdBy, createdAt, lastRunAt, team (server-managed)
    const { organization, createdBy, createdAt, lastRunAt, team, ...cleanRule } = rule as any;
    await dispatch(updateRule({ ...cleanRule, status: !rule.status }));
    dispatch(fetchDistributionRules());
  };

  const handleDelete = async (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this rule?')) {
      await dispatch(deleteRule(ruleId));
      dispatch(fetchDistributionRules());
    }
  };

  const handleEdit = (rule: DistributionRule) => {
    setEditingRule(rule);
    setShowForm(true);
  };

  const handleSave = async (rule: DistributionRule) => {
    // Ensure server-managed fields are not sent
    const { organization, createdBy, createdAt, lastRunAt, team, totalDistributed, totalNoOfRun, ...cleanRule } = rule as any;

    if (editingRule) {
      await dispatch(updateRule({ ...cleanRule, _id: editingRule._id }));
    } else {
      await dispatch(createRule(cleanRule));
    }
    dispatch(fetchDistributionRules());
    setShowForm(false);
    setEditingRule(null);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingRule(null);
  };

  // Filter rules based on search
  const filteredRules = React.useMemo(() => {
    if (!ruleSearch.trim()) return rules;
    const searchLower = ruleSearch.toLowerCase();
    return rules.filter((rule: DistributionRule) => {
      const sourceName = sources.find((s: any) => s.value === rule.integration)?.label || '';
      return (
        rule.name?.toLowerCase().includes(searchLower) ||
        sourceName.toLowerCase().includes(searchLower) ||
        rule.createdBy?.toLowerCase().includes(searchLower)
      );
    });
  }, [rules, ruleSearch, sources]);

  if (showForm) {
    return <RuleForm onCancel={handleCancel} onSave={handleSave} editRule={editingRule} sources={sources} />;
  }

  return (
    <div className="settings-content-wrapper">
      <div className="ds-flex ds-items-center ds-justify-between" style={{ marginBottom: '1rem' }}>
        <div>
          <h2 className="ds-h2" style={{ margin: 0, fontFamily: 'var(--font-family-primary)' }}>Lead Distribution Rules</h2>
          <p className="ds-helper-text" style={{ margin: '0.5rem 0 0 0', maxWidth: '640px', fontSize: '13px' }}>
            Automate lead assignment based on source and criteria.
          </p>
        </div>
        <button className="ds-btn ds-btn-primary" onClick={() => setShowForm(true)} style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', padding: '0.5rem 1rem' }}>
          <Plus size={16} />
          Create Rule
        </button>
      </div>

      {/* Search Bar */}
      {rules.length > 0 && (
        <div style={{ position: 'relative', marginBottom: '1rem', maxWidth: '320px' }}>
          <input
            type="text"
            value={ruleSearch}
            onChange={(e) => setRuleSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            placeholder="Search rules..."
            className="ds-input"
            style={{ paddingLeft: '2.25rem', fontSize: '13px', padding: '0.5rem 0.75rem 0.5rem 2.25rem' }}
          />
          <Search
            size={14}
            style={{
              position: 'absolute',
              left: '0.75rem',
              top: '50%',
              transform: 'translateY(-50%)',
              color: 'var(--color-neutral-400)'
            }}
          />
        </div>
      )}

      <div className="ds-flex ds-flex-col">
        {loading.rules ? (
          <div className="ds-flex ds-items-center ds-justify-center" style={{ padding: '2rem' }}>
            <p className="ds-body ds-text-secondary">Loading rules...</p>
          </div>
        ) : rules.length === 0 ? (
          <div className="ds-card" style={{ padding: '2rem' }}>
            <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{ textAlign: 'center' }}>
              <GitBranch size={48} style={{ color: 'var(--color-neutral-300)', marginBottom: '1rem' }} />
              <h3 className="ds-h4" style={{ margin: '0 0 0.5rem 0' }}>No distribution rules</h3>
              <p className="ds-body-sm ds-text-secondary" style={{ margin: '0 0 1.5rem 0' }}>
                Create rules to automatically distribute leads
              </p>
              <button className="ds-btn ds-btn-primary" onClick={() => setShowForm(true)} style={{ fontSize: '13px' }}>
                <Plus size={14} />
                Create Your First Rule
              </button>
            </div>
          </div>
        ) : filteredRules.length === 0 ? (
          <div className="ds-card" style={{ padding: '1.5rem', textAlign: 'center' }}>
            <Search size={32} style={{ color: 'var(--color-neutral-300)', marginBottom: '0.75rem' }} />
            <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
              No rules match "{ruleSearch}"
            </p>
          </div>
        ) : (
          filteredRules.map((rule: DistributionRule) => {
            const assignedMembers = rule.recipientsIds?.ids
              ? Object.keys(rule.recipientsIds.ids).filter(id => rule.recipientsIds!.ids[id].status)
              : [];
            const assignedTeamMembers = teamMembers?.filter((m: any) => assignedMembers.includes(m._id)) || [];
            const fieldValues = (rule.ruleConfig?.fieldValue || rule.ruleConfig?.custom?.[1]?.value || '').split(',').filter((v: string) => v.trim());

            return (
              <div
                key={rule._id}
                className="ds-card ds-cursor-pointer ds-transition distribution-rule-card"
                onClick={() => handleEdit(rule)}
                style={{
                  padding: '0.875rem 1rem',
                  marginBottom: '0.5rem',
                  borderLeft: rule.status ? '3px solid var(--color-success)' : '3px solid var(--color-neutral-300)',
                }}
              >
                {/* Single Row Layout */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '1.5rem', alignItems: 'center' }}>
                  {/* Column 1: Name + Source + Conditions */}
                  <div style={{ minWidth: 0 }}>
                    <div className="ds-flex ds-items-center ds-gap-2" style={{ marginBottom: '0.375rem' }}>
                      <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rule.name}</h4>
                      <span
                        style={{
                          fontSize: '10px',
                          padding: '1px 6px',
                          borderRadius: '10px',
                          background: rule.status ? 'rgba(16, 185, 129, 0.15)' : 'rgba(0,0,0,0.08)',
                          color: rule.status ? 'var(--color-success)' : 'var(--text-tertiary)',
                          fontWeight: 500,
                          flexShrink: 0
                        }}
                      >
                        {rule.status ? 'Active' : 'Off'}
                      </span>
                    </div>
                    <div className="ds-flex ds-items-center ds-gap-2 ds-flex-wrap" style={{ fontSize: '12px' }}>
                      <span style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                        {sources.find((s: any) => s.value === rule.integration)?.label || 'Unknown'}
                      </span>
                      {!rule.ruleConfig?.fieldName && !rule.ruleConfig?.custom?.[1]?.key ? (
                        <span style={{ color: 'var(--color-success)', fontSize: '11px' }}>• All leads</span>
                      ) : (
                        <>
                          <span style={{ color: 'var(--text-tertiary)' }}>→</span>
                          <span style={{ color: 'var(--text-secondary)' }}>
                            {(() => {
                              const fieldName = rule.ruleConfig?.fieldName || rule.ruleConfig?.custom?.[1]?.key || '';
                              if (rule.integration === FACEBOOK_INTEGRATION_ID && fieldName.toLowerCase() === 'form') return 'Form';
                              if (rule.integration === FACEBOOK_INTEGRATION_ID && fieldName.toLowerCase() === 'page') return 'Page';
                              return fieldName;
                            })()}
                          </span>
                          {fieldValues.length > 0 && (
                            <span style={{ color: 'var(--text-tertiary)', fontSize: '11px' }}>
                              ({fieldValues.length} value{fieldValues.length !== 1 ? 's' : ''})
                            </span>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Column 2: Team Members */}
                  <div style={{ minWidth: '120px' }}>
                    <div className="ds-flex ds-items-center ds-gap-1">
                      <Users size={12} style={{ color: 'var(--color-neutral-400)' }} />
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {assignedTeamMembers.length > 0 ? (
                          <>
                            {assignedTeamMembers.slice(0, 2).map((m: any) => m.firstName).join(', ')}
                            {assignedTeamMembers.length > 2 && <span style={{ color: 'var(--text-tertiary)' }}> +{assignedTeamMembers.length - 2}</span>}
                          </>
                        ) : (
                          <span style={{ color: 'var(--text-tertiary)' }}>No members</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Column 3: Stats */}
                  <div style={{ display: 'flex', gap: '1.25rem', textAlign: 'center' }}>
                    <div>
                      <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--color-primary)', lineHeight: 1 }}>{rule.totalDistributed || 0}</p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: 'var(--text-tertiary)' }}>assigned</p>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: 'var(--text-secondary)', lineHeight: 1 }}>{rule.totalNoOfRun || 0}</p>
                      <p style={{ margin: '2px 0 0 0', fontSize: '10px', color: 'var(--text-tertiary)' }}>runs</p>
                    </div>
                  </div>

                  {/* Column 4: Actions */}
                  <div className="ds-flex ds-items-center ds-gap-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="ds-btn ds-btn-icon"
                      onClick={(e) => { e.stopPropagation(); handleDelete(rule._id!); }}
                      title="Delete"
                      style={{ color: 'var(--color-error)', padding: '4px', opacity: 0.7 }}
                    >
                      <Trash2 size={14} />
                    </button>
                    <label className="switch" style={{ transform: 'scale(0.8)' }}>
                      <input
                        type="checkbox"
                        checked={rule.status || false}
                        onChange={(e) => { e.stopPropagation(); handleToggleStatus(rule); }}
                      />
                      <span className="slider"></span>
                    </label>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

interface RuleFormProps {
  onCancel: () => void;
  onSave: (rule: DistributionRule) => void;
  editRule: DistributionRule | null;
  sources: { value: string; label: string; isCustom?: boolean }[];
}

// Step definitions for the wizard
const STEPS = [
  { id: 1, title: 'Name', description: 'Give your rule a name' },
  { id: 2, title: 'Source', description: 'Select lead source' },
  { id: 3, title: 'Conditions', description: 'Add filter conditions' },
  { id: 4, title: 'Team', description: 'Assign team members' },
];

const RuleForm: React.FC<RuleFormProps> = ({ onCancel, onSave, editRule, sources }) => {
  const dispatch = useDispatch<AppDispatch>();
  const teamMembers = useSelector((state: RootState) => state.whatsapp.team.members);
  const StoreData = useSelector((state: any) => state.rootReducers);

  // Wizard step state
  const [currentStep, setCurrentStep] = useState(() => {
    logger.log('🧙 RuleForm init - editRule:', editRule ? 'YES' : 'NO');
    return 1;
  });
  const [maxVisitedStep, setMaxVisitedStep] = useState(1);
  const [useFieldConditions, setUseFieldConditions] = useState<boolean | null>(null);

  // Log step changes
  useEffect(() => {
    logger.log(`📍 Step changed to: ${currentStep}, maxVisited: ${maxVisitedStep}`);
  }, [currentStep, maxVisitedStep]);

  // Get custom lead form fields
  const customLeadFormFields = React.useMemo(() => {
    const customForm = StoreData?.userData?.userDetails?.userPreference?.customForm;
    logger.log('📋 Custom form data from Redux:', customForm);

    if (!customForm) {
      logger.log('⚠️ No custom form data found in Redux');
      return [];
    }

    // Filter out specific types (matching mobile logic)
    const filtered = customForm.filter((field: any) =>
      field.type !== 'location' &&
      field.type !== 'date' &&
      field.type !== 'time' &&
      field.type !== 'name' &&
      field.type !== 'email' &&
      field.type !== 'phone'
    );

    logger.log(`✅ Filtered custom lead form fields (${filtered.length}):`, filtered);
    return filtered;
  }, [StoreData?.userData?.userDetails?.userPreference?.customForm]);

  const [formData, setFormData] = useState(() => {
    if (editRule) {
      logger.log('🔍 Loading edit rule:', editRule);
      logger.log('📋 ruleConfig structure:', editRule.ruleConfig);

      const fieldName = editRule.ruleConfig?.fieldName || editRule.ruleConfig?.custom?.[1]?.key || '';
      const fieldValue = editRule.ruleConfig?.fieldValue || editRule.ruleConfig?.custom?.[1]?.value || '';
      const operator = editRule.ruleConfig?.custom?.[1]?.operator || 'eq';

      logger.log('✅ Extracted values:', { fieldName, fieldValue, operator });

      return {
        name: editRule.name || '',
        source: editRule.integration || '',
        fieldName,
        operator,
        values: fieldValue.split(',').filter((v: string) => v.trim()),
        currentValue: '',
      };
    }

    return {
      name: '',
      source: '',
      fieldName: '',
      operator: 'eq',
      values: [] as string[],
      currentValue: '',
    };
  });

  // Initialize useFieldConditions and maxVisitedStep based on editRule
  useEffect(() => {
    if (editRule) {
      const hasFieldConditions = !!(editRule.ruleConfig?.fieldName || editRule.ruleConfig?.custom?.[1]?.key);
      logger.log('📝 Edit mode init:', {
        ruleName: editRule.name,
        hasFieldConditions,
        integration: editRule.integration,
        recipientsCount: Object.keys(editRule.recipientsIds?.ids || {}).length
      });
      setUseFieldConditions(hasFieldConditions);
      // When editing, all steps should be accessible
      setMaxVisitedStep(4);
    }
  }, [editRule]);

  const [selectedMembers, setSelectedMembers] = useState<{ [key: string]: boolean }>(() => {
    if (editRule?.recipientsIds?.ids) {
      const members = Object.keys(editRule.recipientsIds.ids).reduce((acc, id) => {
        acc[id] = editRule.recipientsIds!.ids[id].status;
        return acc;
      }, {} as { [key: string]: boolean });
      logger.log('👥 Init selectedMembers from editRule:', Object.keys(members).length, 'members');
      return members;
    }
    return {};
  });

  const [error, setError] = useState<string>('');
  const [teamMemberSearch, setTeamMemberSearch] = useState<string>('');

  // Facebook dynamic fields state
  const [facebookOptions, setFacebookOptions] = useState<{
    pages: Array<{ pageId: string; pageName: string; pageAccessToken: string }>;
    forms: Array<{ id: string; name: string; locale: string; status: string }>;
  } | null>(null);
  const [loadingFacebookOptions, setLoadingFacebookOptions] = useState(false);

  // Component mount/unmount tracking
  useEffect(() => {
    logger.log('🚀 RuleForm MOUNTED', { isEdit: !!editRule, ruleName: editRule?.name });
    return () => {
      logger.log('💨 RuleForm UNMOUNTED');
    };
  }, []);

  useEffect(() => {
    // Fetch team members if not loaded
    if (!teamMembers || teamMembers.length === 0) {
      dispatch({ type: 'team/fetchMembers' });
    }
  }, [dispatch, teamMembers]);

  // Fetch Facebook options when Facebook is selected as source
  useEffect(() => {
    const fetchFacebookOptions = async () => {
      if (formData.source === FACEBOOK_INTEGRATION_ID) {
        setLoadingFacebookOptions(true);
        try {
          logger.log('🔍 Fetching Facebook distribution options...');
          const response = await getDistributionOptions();

          if (response?.data?.data) {
            logger.log('✅ Facebook options received:', response.data.data);
            setFacebookOptions({
              pages: response.data.data.pages || [],
              forms: response.data.data.forms || []
            });
          }
        } catch (err) {
          logger.error('❌ Failed to fetch Facebook options:', err);
          setFacebookOptions({ pages: [], forms: [] });
        } finally {
          setLoadingFacebookOptions(false);
        }
      } else {
        // Clear Facebook options when other source is selected
        setFacebookOptions(null);
      }
    };

    fetchFacebookOptions();
  }, [formData.source]);

  // Step validation
  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return formData.name.trim().length > 0;
      case 2:
        return formData.source.length > 0;
      case 3:
        // Conditions step is always valid (user can skip conditions)
        if (useFieldConditions === false) return true;
        if (useFieldConditions === null) return false;
        // If using conditions, validate them
        if (formData.source === FACEBOOK_INTEGRATION_ID) {
          return formData.fieldName.trim().length > 0 && formData.values.length > 0;
        }
        if (formData.source === LEAD_FORM_INTEGRATION_ID) {
          return formData.fieldName.trim().length > 0 && formData.values.length > 0;
        }
        return formData.fieldName.trim().length > 0 && formData.values.length > 0;
      case 4:
        return Object.keys(selectedMembers).filter(id => selectedMembers[id]).length > 0;
      default:
        return false;
    }
  };

  const canProceed = (): boolean => {
    return isStepValid(currentStep);
  };

  const handleNext = () => {
    logger.log(`➡️ handleNext called - currentStep: ${currentStep}, canProceed: ${canProceed()}`);
    if (canProceed() && currentStep < 4) {
      // Clear any previous errors when advancing
      setError('');
      let nextStep: number;
      // If on conditions step and user chose "no conditions", skip validation
      if (currentStep === 3 && useFieldConditions === false) {
        nextStep = 4;
        logger.log('⏭️ Skipping conditions (useFieldConditions=false), going to step 4');
      } else {
        nextStep = currentStep + 1;
      }
      logger.log(`➡️ Moving to step ${nextStep}`);
      setCurrentStep(nextStep);
      // Track the highest step visited
      setMaxVisitedStep(prev => Math.max(prev, nextStep));
    }
  };

  const handleBack = () => {
    logger.log(`⬅️ handleBack called - currentStep: ${currentStep}`);
    if (currentStep > 1) {
      setError('');
      const prevStep = currentStep - 1;
      logger.log(`⬅️ Moving back to step ${prevStep}`);
      setCurrentStep(prevStep);
    }
  };

  const goToStep = (step: number) => {
    logger.log(`🔢 goToStep called - target: ${step}, maxVisited: ${maxVisitedStep}`);
    // Allow going to any step that has been visited
    if (step >= 1 && step <= maxVisitedStep) {
      logger.log(`🔢 Jumping to step ${step}`);
      setError('');
      setCurrentStep(step);
    } else {
      logger.log(`🚫 Cannot go to step ${step} - not yet visited`);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    logger.log('📤 handleSubmit called', { formData, selectedMembers, useFieldConditions });

    // Validate form
    const validationMsg = getValidationMessage();
    if (validationMsg) {
      logger.log('❌ Validation failed:', validationMsg);
      setError(validationMsg);
      return;
    }
    logger.log('✅ Validation passed, saving rule...');

    // Clear any existing errors
    setError('');

    const recipientsIds: any = {
      ids: {},
      cursor: editRule?.recipientsIds?.cursor || '',
      current_distribution: editRule?.recipientsIds?.current_distribution || '',
    };

    Object.keys(selectedMembers).forEach((id) => {
      if (selectedMembers[id]) {
        recipientsIds.ids[id] = { waitage: 1, status: true };
      }
    });

    const selectedUserIds = Object.keys(selectedMembers).filter(id => selectedMembers[id]);

    // Build ruleConfig based on whether conditions are used
    let ruleConfig: any;

    if (useFieldConditions === false || !formData.fieldName) {
      // Simple rule: No field conditions - just source → users
      ruleConfig = {
        custom: [
          {
            integration: formData.source,
            users: selectedUserIds,
          },
        ],
      };
    } else {
      // Complex rule: With field conditions
      ruleConfig = {
        custom: [
          {
            integration: formData.source,
            users: selectedUserIds,
          },
          {
            key: formData.fieldName,
            value: formData.values.join(','),
            operator: formData.operator,
            users: selectedUserIds,
          },
        ],
      };
    }

    // Only include fields needed for create/update
    // Exclude: organization, createdBy, createdAt, lastRunAt, team (server-managed)
    const rule: DistributionRule = {
      name: formData.name,
      integration: formData.source,
      ruleConfig,
      recipientsIds,
      status: editRule?.status ?? true,
    };

    onSave(rule);
  };

  const addValue = () => {
    if (formData.currentValue.trim()) {
      setFormData((prev) => ({
        ...prev,
        values: [...prev.values, prev.currentValue.trim()],
        currentValue: '',
      }));
    }
  };

  const removeValue = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      values: prev.values.filter((_, i) => i !== index),
    }));
  };

  const toggleMember = (memberId: string) => {
    setSelectedMembers((prev) => ({
      ...prev,
      [memberId]: !prev[memberId],
    }));
  };

  // Validation helper
  const getValidationMessage = (): string => {
    if (!formData.name.trim()) return 'Rule name is required';
    if (!formData.source) return 'Please select a lead source';

    // If user chose to use field conditions, validate them
    if (useFieldConditions === true) {
      // Facebook-specific validation
      if (formData.source === FACEBOOK_INTEGRATION_ID) {
        if (!formData.fieldName.trim()) {
          return 'Please select either "Form Name" or "Page Name"';
        }
        if (formData.values.length === 0) {
          if (formData.fieldName === 'form') {
            return 'Please select at least one Facebook form';
          } else if (formData.fieldName === 'page') {
            return 'Please select at least one Facebook page';
          }
          return 'Please add at least one value';
        }
      }

      // Lead Form-specific validation
      if (formData.source === LEAD_FORM_INTEGRATION_ID) {
        if (!formData.fieldName.trim()) {
          return 'Please select a field from your lead form';
        }
        if (formData.values.length === 0) {
          return 'Please add at least one value for the selected field';
        }
      }

      // For other sources with field configuration
      const isSpecialSource = formData.source === FACEBOOK_INTEGRATION_ID ||
                              formData.source === LEAD_FORM_INTEGRATION_ID;

      if (!isSpecialSource && !formData.fieldName.trim()) {
        return 'Field name is required when using conditions';
      }

      if (!isSpecialSource && formData.values.length === 0) {
        return 'Please add at least one value for the condition';
      }
    }

    // If user hasn't made a choice about conditions yet (on step 3)
    if (useFieldConditions === null && currentStep === 3) {
      return 'Please choose whether to use conditions or not';
    }

    if (Object.keys(selectedMembers).filter(id => selectedMembers[id]).length === 0) {
      return 'Please select at least one team member to distribute leads to';
    }
    return '';
  };

  const isFormValid = (): boolean => {
    return getValidationMessage() === '';
  };

  // Filter team members based on search
  const filteredTeamMembers = React.useMemo(() => {
    if (!teamMembers) return [];

    const activeMembers = teamMembers.filter((member: any) => member.isActive);

    if (!teamMemberSearch.trim()) return activeMembers;

    const searchLower = teamMemberSearch.toLowerCase();
    return activeMembers.filter((member: any) =>
      `${member.firstName} ${member.lastName}`.toLowerCase().includes(searchLower)
    );
  }, [teamMembers, teamMemberSearch]);

  // Render Step Content
  const renderStepContent = () => {
    logger.log(`🎨 Rendering step ${currentStep}, useFieldConditions: ${useFieldConditions}`);
    switch (currentStep) {
      case 1:
        return (
          <div className="step-content">
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <GitBranch size={28} style={{ color: '#fff' }} />
              </div>
              <h3 className="ds-h3" style={{ margin: '0 0 0.5rem 0' }}>Name Your Rule</h3>
              <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
                Give your distribution rule a descriptive name
              </p>
            </div>
            <div className="ds-form-group" style={{ maxWidth: '400px', margin: '0 auto' }}>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleNext();
                  }
                }}
                placeholder="e.g., Facebook Leads to Sales Team"
                className="ds-input"
                style={{ fontSize: 'var(--font-size-lg)', padding: '1rem', textAlign: 'center' }}
                autoFocus
              />
            </div>
          </div>
        );

      case 2:
        return (
          <div className="step-content">
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 className="ds-h3" style={{ margin: '0 0 0.5rem 0' }}>Select Lead Source</h3>
              <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
                Choose which integration this rule applies to
              </p>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
              gap: '1rem',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              {sources.map((source: { value: string; label: string }) => (
                <button
                  key={source.value}
                  type="button"
                  onClick={() => {
                    setFormData((prev) => ({ ...prev, source: source.value, fieldName: '', values: [], currentValue: '' }));
                    // Reset field conditions choice when source changes
                    setUseFieldConditions(null);
                  }}
                  className="ds-card ds-cursor-pointer ds-transition"
                  style={{
                    padding: '1.25rem',
                    textAlign: 'center',
                    border: formData.source === source.value
                      ? '2px solid var(--color-primary)'
                      : '2px solid var(--border-light)',
                    background: formData.source === source.value
                      ? 'rgba(102, 126, 234, 0.08)'
                      : 'var(--bg-primary)',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <p className="ds-body ds-font-medium" style={{
                    margin: 0,
                    color: formData.source === source.value ? 'var(--color-primary)' : 'var(--text-primary)'
                  }}>
                    {source.label}
                  </p>
                </button>
              ))}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="step-content">
            <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
              <h3 className="ds-h3" style={{ margin: '0 0 0.5rem 0' }}>Add Filter Conditions?</h3>
              <p className="ds-body-sm ds-text-secondary" style={{ margin: '0 auto', maxWidth: '500px' }}>
                You can distribute all leads from <strong>{sources.find(s => s.value === formData.source)?.label}</strong>, or add specific conditions
              </p>
            </div>

            {/* Choice: With or Without Conditions */}
            {useFieldConditions === null && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '1.5rem',
                maxWidth: '600px',
                margin: '0 auto'
              }}>
                <button
                  type="button"
                  onClick={() => setUseFieldConditions(false)}
                  className="ds-card ds-cursor-pointer ds-transition"
                  style={{
                    padding: '2rem',
                    textAlign: 'center',
                    border: '2px solid var(--border-light)',
                    background: 'var(--bg-primary)',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}>
                    <Users size={24} style={{ color: '#fff' }} />
                  </div>
                  <h4 className="ds-h4" style={{ margin: '0 0 0.5rem 0' }}>All Leads</h4>
                  <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
                    Distribute every lead from this source
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => setUseFieldConditions(true)}
                  className="ds-card ds-cursor-pointer ds-transition"
                  style={{
                    padding: '2rem',
                    textAlign: 'center',
                    border: '2px solid var(--border-light)',
                    background: 'var(--bg-primary)',
                    cursor: 'pointer'
                  }}
                >
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 1rem'
                  }}>
                    <GitBranch size={24} style={{ color: '#fff' }} />
                  </div>
                  <h4 className="ds-h4" style={{ margin: '0 0 0.5rem 0' }}>With Conditions</h4>
                  <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
                    Filter by specific field values
                  </p>
                </button>
              </div>
            )}

            {/* No Conditions - Confirmation */}
            {useFieldConditions === false && (
              <div style={{ maxWidth: '500px', margin: '0 auto', textAlign: 'center' }}>
                <div className="ds-card" style={{
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.2)',
                  padding: '2rem'
                }}>
                  <Users size={48} style={{ color: 'var(--color-success)', marginBottom: '1rem' }} />
                  <h4 className="ds-h4" style={{ margin: '0 0 0.5rem 0', color: 'var(--color-success)' }}>
                    All Leads Mode
                  </h4>
                  <p className="ds-body-sm ds-text-secondary" style={{ margin: '0 0 1rem 0' }}>
                    Every lead from <strong>{sources.find(s => s.value === formData.source)?.label}</strong> will be distributed to your selected team members.
                  </p>
                  <button
                    type="button"
                    className="ds-btn ds-btn-ghost"
                    onClick={() => setUseFieldConditions(null)}
                    style={{ fontSize: 'var(--font-size-sm)' }}
                  >
                    Change selection
                  </button>
                </div>
              </div>
            )}

            {/* With Conditions - Show field configuration */}
            {useFieldConditions === true && (
              <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                <div style={{ marginBottom: '1rem' }}>
                  <button
                    type="button"
                    className="ds-btn ds-btn-ghost"
                    onClick={() => setUseFieldConditions(null)}
                    style={{ fontSize: 'var(--font-size-sm)', padding: '0.25rem 0' }}
                  >
                    ← Change selection
                  </button>
                </div>

                <div className="ds-form-group">
                  <label className="ds-label">Field Name</label>
                  {/* Facebook - Show Form/Page dropdown */}
                  {formData.source === FACEBOOK_INTEGRATION_ID ? (
                    <select
                      value={formData.fieldName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value, values: [], currentValue: '' }))}
                      className="ds-select"
                      disabled={loadingFacebookOptions}
                    >
                      <option value="">Select field</option>
                      <option value="form">Form Name</option>
                      <option value="page">Page Name</option>
                    </select>
                  ) : /* Lead Form - Show custom form fields */
                  formData.source === LEAD_FORM_INTEGRATION_ID && customLeadFormFields.length > 0 ? (
                    <select
                      value={formData.fieldName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value, values: [], currentValue: '' }))}
                      className="ds-select"
                    >
                      <option value="">Select field</option>
                      {customLeadFormFields.map((field: any) => (
                        <option key={field.value} value={field.value}>
                          {field.name}
                        </option>
                      ))}
                    </select>
                  ) : /* Other sources - Text input */
                  (
                    <input
                      type="text"
                      value={formData.fieldName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, fieldName: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                      placeholder="Enter field name"
                      className="ds-input"
                    />
                  )}
                </div>

                {/* Operator - Only show for non-Facebook form/page */}
                {!(formData.source === FACEBOOK_INTEGRATION_ID && (formData.fieldName === 'form' || formData.fieldName === 'page')) && (
                  <div className="ds-form-group">
                    <label className="ds-label">Operator</label>
                    <select
                      value={formData.operator}
                      onChange={(e) => setFormData((prev) => ({ ...prev, operator: e.target.value }))}
                      className="ds-select"
                    >
                      {OPERATORS.map((op) => (
                        <option key={op.value} value={op.value}>
                          {op.label}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Values */}
                <div className="ds-form-group">
                  <label className="ds-label">Values</label>
                  {/* Lead Form with field options - Show options dropdown */}
                  {formData.source === LEAD_FORM_INTEGRATION_ID && formData.fieldName &&
                   customLeadFormFields.find((f: any) => f.value === formData.fieldName)?.options?.length ? (
                    <>
                      <div className="ds-flex ds-gap-2">
                        <select
                          value={formData.currentValue}
                          onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                          className="ds-select"
                          style={{ flex: 1 }}
                        >
                          <option value="">Select value</option>
                          {customLeadFormFields
                            .find((f: any) => f.value === formData.fieldName)
                            ?.options?.map((option: string) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                        </select>
                        <button
                          type="button"
                          className="ds-btn ds-btn-primary"
                          onClick={addValue}
                          disabled={!formData.currentValue}
                        >
                          Add
                        </button>
                      </div>
                    </>
                  ) : /* Facebook Form Name */
                  formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'form' ? (
                    <div className="ds-flex ds-gap-2">
                      <select
                        value={formData.currentValue}
                        onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                        className="ds-select"
                        disabled={loadingFacebookOptions || !facebookOptions}
                        style={{ flex: 1 }}
                      >
                        <option value="">
                          {loadingFacebookOptions ? 'Loading forms...' : 'Select Facebook form'}
                        </option>
                        {facebookOptions?.forms?.map((form) => (
                          <option key={form.id} value={`${form.name} (${form.id})`}>
                            {form.name} ({form.id})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="ds-btn ds-btn-primary"
                        onClick={addValue}
                        disabled={!formData.currentValue}
                      >
                        Add
                      </button>
                    </div>
                  ) : /* Facebook Page Name */
                  formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'page' ? (
                    <div className="ds-flex ds-gap-2">
                      <select
                        value={formData.currentValue}
                        onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                        className="ds-select"
                        disabled={loadingFacebookOptions || !facebookOptions}
                        style={{ flex: 1 }}
                      >
                        <option value="">
                          {loadingFacebookOptions ? 'Loading pages...' : 'Select Facebook page'}
                        </option>
                        {facebookOptions?.pages?.map((page) => (
                          <option key={page.pageId} value={`${page.pageName} (${page.pageId})`}>
                            {page.pageName} ({page.pageId})
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="ds-btn ds-btn-primary"
                        onClick={addValue}
                        disabled={!formData.currentValue}
                      >
                        Add
                      </button>
                    </div>
                  ) : /* Regular text input */
                  (
                    <div className="ds-flex ds-gap-2">
                      <input
                        type="text"
                        value={formData.currentValue}
                        onChange={(e) => setFormData((prev) => ({ ...prev, currentValue: e.target.value }))}
                        placeholder="Type and press Add"
                        className="ds-input"
                        style={{ flex: 1 }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            addValue();
                          }
                        }}
                      />
                      <button type="button" className="ds-btn ds-btn-primary" onClick={addValue}>
                        Add
                      </button>
                    </div>
                  )}

                  {/* Display added values as badges */}
                  {formData.values.length > 0 && (
                    <div className="ds-flex ds-flex-wrap ds-gap-2" style={{ marginTop: '0.75rem' }}>
                      {formData.values.map((value, index) => (
                        <span key={index} className="ds-badge ds-badge-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 0.75rem' }}>
                          {value}
                          <button
                            type="button"
                            onClick={() => removeValue(index)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: 'inherit',
                              fontSize: '1.25rem',
                              cursor: 'pointer',
                              padding: 0,
                              lineHeight: 1
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        );

      case 4: {
        const selectedCount = Object.keys(selectedMembers).filter(id => selectedMembers[id]).length;
        return (
          <div className="step-content">
            {/* Header Row */}
            <div className="ds-flex ds-items-center ds-justify-between" style={{ marginBottom: '1rem' }}>
              <div>
                <h3 className="ds-h4" style={{ margin: '0 0 0.25rem 0' }}>Select Team Members</h3>
                <p className="ds-body-sm ds-text-secondary" style={{ margin: 0 }}>
                  Choose who should receive leads from this rule
                </p>
              </div>
              {selectedCount > 0 && (
                <div className="ds-flex ds-items-center ds-gap-3">
                  <span style={{ fontSize: '13px', color: 'var(--color-primary)', fontWeight: 500 }}>
                    {selectedCount} selected
                  </span>
                  <button
                    type="button"
                    className="ds-btn ds-btn-ghost"
                    onClick={() => setSelectedMembers({})}
                    style={{ fontSize: '12px', padding: '0.25rem 0.5rem' }}
                  >
                    Clear
                  </button>
                </div>
              )}
            </div>

            {/* Search Box - Full Width */}
            {teamMembers && teamMembers.filter((m: any) => m.isActive).length > 0 && (
              <div style={{ position: 'relative', marginBottom: '1rem' }}>
                <input
                  type="text"
                  value={teamMemberSearch}
                  onChange={(e) => setTeamMemberSearch(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
                  placeholder="Search team members..."
                  className="ds-input"
                  style={{ paddingLeft: '2.25rem', fontSize: '13px' }}
                />
                <Search
                  size={14}
                  style={{
                    position: 'absolute',
                    left: '0.75rem',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: 'var(--color-neutral-400)'
                  }}
                />
              </div>
            )}

            {/* Team Members Grid */}
            <div style={{
              maxHeight: '320px',
              overflowY: 'auto',
              border: '1px solid var(--border-light)',
              borderRadius: '8px',
              background: 'var(--bg-primary)',
              padding: '0.5rem'
            }}>
              {!teamMembers || teamMembers.filter((m: any) => m.isActive).length === 0 ? (
                <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{ padding: '2rem', textAlign: 'center' }}>
                  <Users size={40} style={{ color: 'var(--color-neutral-300)', marginBottom: '0.75rem' }} />
                  <h4 className="ds-h5" style={{ margin: '0 0 0.25rem 0' }}>No Team Members</h4>
                  <p className="ds-body-sm ds-text-tertiary" style={{ margin: 0 }}>
                    Add team members to distribute leads.
                  </p>
                </div>
              ) : filteredTeamMembers.length === 0 ? (
                <div className="ds-flex ds-flex-col ds-items-center ds-justify-center" style={{ padding: '2rem', textAlign: 'center' }}>
                  <Search size={32} style={{ color: 'var(--color-neutral-300)', marginBottom: '0.5rem' }} />
                  <p className="ds-body-sm ds-text-tertiary" style={{ margin: 0 }}>
                    No members match "{teamMemberSearch}"
                  </p>
                </div>
              ) : (
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: '0.5rem'
                }}>
                  {filteredTeamMembers.map((member: any) => (
                    <label
                      key={member._id}
                      className="ds-cursor-pointer ds-transition"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.625rem',
                        padding: '0.625rem 0.75rem',
                        borderRadius: '6px',
                        border: selectedMembers[member._id]
                          ? '1px solid var(--color-primary)'
                          : '1px solid var(--border-light)',
                        background: selectedMembers[member._id]
                          ? 'rgba(102, 126, 234, 0.08)'
                          : 'var(--bg-secondary)',
                        transition: 'all 0.15s ease'
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedMembers[member._id] || false}
                        onChange={() => toggleMember(member._id)}
                        style={{
                          width: '16px',
                          height: '16px',
                          cursor: 'pointer',
                          accentColor: 'var(--color-primary)'
                        }}
                      />
                      <span style={{
                        fontSize: '13px',
                        fontWeight: selectedMembers[member._id] ? 500 : 400,
                        color: selectedMembers[member._id] ? 'var(--color-primary)' : 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {member.firstName} {member.lastName}
                      </span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // Get summary text for the preview
  const getPreviewSummary = () => {
    const sourceName = sources.find(s => s.value === formData.source)?.label || 'Unknown';
    const memberCount = Object.keys(selectedMembers).filter(id => selectedMembers[id]).length;

    if (useFieldConditions === false || !formData.fieldName) {
      return `All leads from ${sourceName} → ${memberCount} team member${memberCount !== 1 ? 's' : ''}`;
    }

    let fieldDisplay = formData.fieldName;
    if (formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'form') {
      fieldDisplay = 'Form Name';
    } else if (formData.source === FACEBOOK_INTEGRATION_ID && formData.fieldName === 'page') {
      fieldDisplay = 'Page Name';
    }

    const valuesDisplay = formData.values.length > 0
      ? formData.values.length > 2
        ? `${formData.values[0]}, +${formData.values.length - 1} more`
        : formData.values.join(', ')
      : '(no values)';

    return `${sourceName} where ${fieldDisplay} = ${valuesDisplay} → ${memberCount} team member${memberCount !== 1 ? 's' : ''}`;
  };

  return (
    <div className="settings-content-wrapper" style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
      {/* Header */}
      <div className="ds-flex ds-items-center ds-gap-3" style={{ marginBottom: '1rem', flexShrink: 0 }}>
        <button className="ds-btn ds-btn-ghost" onClick={onCancel} style={{ fontSize: '13px', padding: '0.375rem 0.75rem' }}>
          <ArrowLeft size={16} />
          Back
        </button>
        <h2 className="ds-h3" style={{ margin: 0 }}>
          {editRule ? 'Edit Rule' : 'Create Rule'}
        </h2>
      </div>

      {error && (
        <div style={{
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.2)',
          borderRadius: '6px',
          padding: '0.625rem 1rem',
          marginBottom: '1rem',
          color: 'var(--color-error)',
          fontSize: '13px',
          flexShrink: 0
        }}>
          {error}
        </div>
      )}

      {/* Stepper - Compact */}
      <div style={{ marginBottom: '1rem', flexShrink: 0 }}>
        <div className="ds-flex ds-items-center ds-justify-center ds-gap-1" style={{ maxWidth: '500px', margin: '0 auto' }}>
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <button
                type="button"
                onClick={() => goToStep(step.id)}
                className="ds-flex ds-items-center ds-gap-2"
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: step.id <= maxVisitedStep ? 'pointer' : 'default',
                  opacity: step.id <= maxVisitedStep ? 1 : 0.5,
                  padding: '0.375rem 0.5rem',
                  borderRadius: '6px',
                  transition: 'all 0.15s ease'
                }}
              >
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: step.id === currentStep
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : step.id <= maxVisitedStep
                    ? 'var(--color-success)'
                    : 'var(--color-neutral-200)',
                  color: step.id <= maxVisitedStep ? '#fff' : 'var(--text-tertiary)',
                  fontWeight: 600,
                  fontSize: '11px'
                }}>
                  {step.id < currentStep ? '✓' : step.id}
                </div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: 500,
                  color: step.id === currentStep ? 'var(--color-primary)' : step.id <= maxVisitedStep ? 'var(--text-primary)' : 'var(--text-secondary)'
                }}>
                  {step.title}
                </span>
              </button>
              {index < STEPS.length - 1 && (
                <div style={{
                  width: '24px',
                  height: '2px',
                  background: step.id < maxVisitedStep ? 'var(--color-success)' : 'var(--color-neutral-200)'
                }} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {/* Step Content - Scrollable */}
        <div className="ds-card" style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', marginBottom: '0' }}>
          <div style={{ flex: 1, padding: '0.75rem 0' }}>
            {renderStepContent()}
          </div>

          {/* Rule Preview - Show after step 2 */}
          {currentStep >= 2 && formData.source && (
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: '6px',
              padding: '0.75rem 1rem',
              marginTop: '1rem'
            }}>
              <div className="ds-flex ds-items-center ds-gap-2">
                <GitBranch size={14} style={{ color: '#fff' }} />
                <p style={{ margin: 0, color: '#fff', fontSize: '12px' }}>
                  <strong>{formData.name || 'Untitled'}</strong>: {getPreviewSummary()}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons - Always Visible / Sticky */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1rem 0',
          marginTop: '0.5rem',
          borderTop: '1px solid var(--border-light)',
          background: 'var(--bg-primary)',
          flexShrink: 0
        }}>
          <button
            type="button"
            className="ds-btn ds-btn-ghost"
            onClick={currentStep === 1 ? onCancel : handleBack}
            style={{ fontSize: '13px', padding: '0.5rem 1rem' }}
          >
            {currentStep === 1 ? 'Cancel' : '← Back'}
          </button>

          <div className="ds-flex ds-gap-2">
            {currentStep < 4 ? (
              <button
                type="button"
                className="ds-btn ds-btn-primary"
                onClick={handleNext}
                disabled={!canProceed()}
                style={{
                  fontSize: '13px',
                  padding: '0.5rem 1.25rem',
                  opacity: canProceed() ? 1 : 0.6,
                  cursor: canProceed() ? 'pointer' : 'not-allowed'
                }}
              >
                {currentStep === 3 && useFieldConditions === false ? 'Skip to Team →' : 'Next →'}
              </button>
            ) : (
              <button
                type="submit"
                className="ds-btn ds-btn-primary"
                disabled={!isFormValid()}
                style={{
                  fontSize: '13px',
                  padding: '0.5rem 1.25rem',
                  opacity: isFormValid() ? 1 : 0.6,
                  cursor: isFormValid() ? 'pointer' : 'not-allowed',
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
                }}
              >
                {editRule ? 'Update Rule' : 'Create Rule'}
              </button>
            )}
          </div>
        </div>
      </form>

    </div>
  );
};

export default LeadDistributionSettings;